"use client";

import { EventSourcePolyfill } from "event-source-polyfill";
import { nanoid } from "nanoid";
import { createContext, useContext, useEffect, useRef } from "react";

import { getAccessToken, refreshTokenAndSaveToLocalStorage } from "@/api/axiosInstance";
import { useBeforeUnload } from "@/hooks/useBeforeUnload";
import { setTimeoutAsync } from "@/utils/common";

interface SSEConnection {
    eventName: string;
    sseEventType: string;
    sseEventFilter: (event: Record<string, unknown>) => boolean;
    sseEventCallback: (event: Record<string, unknown>) => void;
    url: string;
}

interface ConnectionState {
    eventSource: EventSourcePolyfill;
    config: SSEConnection;
    retryCount: number;
}

interface SSEContextProp {
    createConnection(config: SSEConnection): string;
    closeConnection(connectionKey: string): void;
}

const SSEContext = createContext<SSEContextProp | undefined>(undefined);

export const useSSE = () => {
  const context = useContext(SSEContext);
  if (!context) {
    throw new Error("useSSE must be used within an SSEProvider");
  }
  return context;
};

export const SSEProvider = ({ children }: { children: React.ReactNode }) => {
  const connectionsRef = useRef<Map<string, ConnectionState>>(new Map());

  useEffect(() => {
    return closeAllConnections;
  }, []);

  useBeforeUnload(() => {
    closeAllConnections();
  });

  const connectSSE = (connectionKey: string, config: SSEConnection, retryCount: number = 0) => {
    const token = getAccessToken();

    const eventSource = new EventSourcePolyfill(config.url, {
      withCredentials: true,
      headers: {
        Authorization: `Bearer ${token}`,
        "Cache-Control": "no-cache",
        "Connection": "keep-alive",
        "Accept": "text/event-stream",
      },
      heartbeatTimeout: 30000,
    });

    eventSource.addEventListener(config.sseEventType, (event) => {
      const connection = connectionsRef.current.get(connectionKey);
      if (!connection) {
        console.warn(`연결을 찾을 수 없음 - ${connectionKey}`);
        return;
      }

      const { sseEventFilter, sseEventCallback } = connection.config;
      try {
        const eventData = JSON.parse(event.data);
        if (!sseEventFilter(eventData)) {
          return;
        }
        sseEventCallback(eventData);
      } catch (error) {
        console.error(`JSON 파싱 에러 - ${connectionKey}:`, error);
      }
    });

    eventSource.onerror = async (errorEvent) => {
      console.error(`SSE 연결 에러 발생 - ${connectionKey}`, errorEvent);

      const connection = connectionsRef.current.get(connectionKey);
      if (connection) {
        connection.eventSource.close();
        connectionsRef.current.delete(connectionKey);
      }

      const currentRetryCount = connection?.retryCount ?? retryCount;

      if (currentRetryCount >= 5) {
        console.error(`SSE 최대 재연결 횟수 초과 - ${connectionKey}`);
        return;
      }

      if (errorEvent.status === 401) {
        await refreshTokenAndSaveToLocalStorage();
      }

      await setTimeoutAsync(3000);
      connectSSE(connectionKey, config, currentRetryCount + 1);
    };

    const existingConnection = connectionsRef.current.get(connectionKey);
    if (existingConnection) {
      existingConnection.eventSource.close();
    }

    connectionsRef.current.set(connectionKey, { eventSource, config, retryCount });
  };

  const createConnection = (config: SSEConnection) => {
    const connectionKey = nanoid(10);
    connectSSE(connectionKey, config);
    return connectionKey;
  };

  const closeConnection = (connectionKey: string) => {
    const connection = connectionsRef.current.get(connectionKey);
    if (connection) {
      connection.eventSource.close();
      connectionsRef.current.delete(connectionKey);
    }
  };

  const closeAllConnections = () => {
    connectionsRef.current.forEach((connection) => {
      connection.eventSource.close();
    });
    connectionsRef.current.clear();
  };

  return (
    <SSEContext.Provider value={{ createConnection, closeConnection }}>
      {children}
    </SSEContext.Provider>
  );
};
